import React from 'react';
import { isUsableExtractedRole } from '../../utils/extractedRole';

/** Keywords that should never appear as the only “topic” chips (broaden follow-ups). */
const BROADEN_KEYWORD_NOISE = new Set([
  'more',
  'candidates',
  'candidate',
  'people',
  'profiles',
  'results',
  'find',
  'show',
  'get',
]);

function isBroadenOnlySearchQuery(q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return false;
  return (
    /^(?:find|show|get|give|list)\s+more\b/i.test(t) ||
    /\bmore\s+candidates\b/i.test(t) ||
    /^(?:more|additional)\s+(?:candidates?|people|profiles|results)\b/i.test(t)
  );
}

/** Loose shape from SSE `brief` (matches backend SearchBrief). */
export type SearchBriefCriteria = {
  tree?: { kind: string; op?: string; children?: unknown[] };
  search_query?: string;
  semantic?: { enabled?: boolean; query?: string; negatives?: string[] };
  confidence?: number;
};

function isCriteriaLeaf(n: unknown): n is {
  kind: 'leaf';
  field: string;
  match: string;
  value: string;
  negate?: boolean;
} {
  if (!n || typeof n !== 'object') return false;
  const o = n as Record<string, unknown>;
  return o.kind === 'leaf' && typeof o.field === 'string' && typeof o.value === 'string';
}

function isCriteriaGroup(n: unknown): n is { kind: string; op: string; children: unknown[] } {
  if (!n || typeof n !== 'object') return false;
  const o = n as Record<string, unknown>;
  return o.kind === 'group' && typeof o.op === 'string' && Array.isArray(o.children);
}

function leafChipClass(field: string): string {
  switch (field) {
    case 'role':
      return 'bg-indigo-600/30 text-indigo-200';
    case 'skill':
      return 'bg-emerald-600/30 text-emerald-200';
    case 'keyword':
      return 'bg-violet-600/25 text-violet-200 border border-violet-500/25';
    case 'location':
    case 'location_model':
      return 'bg-amber-600/30 text-amber-200';
    case 'seniority':
      return 'bg-purple-600/30 text-purple-200';
    case 'language':
      return 'bg-rose-600/25 text-rose-200 border border-rose-500/25';
    case 'team':
    case 'project':
      return 'bg-gray-600/30 text-gray-200';
    case 'ai_tool':
      return 'bg-sky-600/30 text-sky-200';
    default:
      return 'bg-slate-600/30 text-slate-200';
  }
}

const BriefLeafChip: React.FC<{
  field: string;
  match: string;
  value: string;
  negate?: boolean;
}> = ({ field, match, value, negate }) => {
  const matchHint = match && match !== 'exact' ? ` (${match})` : '';
  const simple = field === 'skill' || field === 'keyword' || field === 'language';
  const display = negate
    ? simple
      ? `NOT ${value}`
      : `NOT ${field}: ${value}`
    : simple
      ? value
      : `${field}: ${value}`;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${leafChipClass(field)} ${
        negate ? 'line-through opacity-90 border border-red-500/40' : ''
      }`}
      title={`${field}${matchHint}${negate ? ' (excluded)' : ''}`}
    >
      {display}
      {!negate && match && match !== 'exact' ? (
        <span className="text-[9px] opacity-70 font-normal uppercase">{match}</span>
      ) : null}
    </span>
  );
};

const BriefTree: React.FC<{ node: unknown; depth?: number }> = ({ node, depth = 0 }) => {
  if (isCriteriaLeaf(node)) {
    return (
      <BriefLeafChip
        field={node.field}
        match={node.match || 'exact'}
        value={node.value}
        negate={node.negate}
      />
    );
  }
  if (isCriteriaGroup(node)) {
    const op = (node.op || 'AND').toUpperCase();
    return (
      <div
        className={`inline-flex flex-wrap items-center gap-1 rounded-lg border border-indigo-500/25 bg-indigo-950/40 px-2 py-1 ${
          depth > 0 ? 'ml-0' : ''
        }`}
      >
        <span className="text-[10px] font-semibold uppercase text-indigo-300/90 px-1">{op}</span>
        {(node.children || []).map((ch, i) => (
          <React.Fragment key={i}>
            {i > 0 && op === 'OR' ? <span className="text-indigo-400/80 text-[10px]">|</span> : null}
            <BriefTree node={ch} depth={depth + 1} />
          </React.Fragment>
        ))}
      </div>
    );
  }
  return null;
};

function briefHasRenderableTree(brief?: SearchBriefCriteria | null): boolean {
  if (!brief?.tree || brief.tree.kind !== 'group') return false;
  const ch = brief.tree.children;
  return Array.isArray(ch) && ch.length > 0;
}

export type AISearchCriteriaPayload = {
  searchQuery: string;
  extracted: {
    role?: string;
    skills_required?: string[];
    skills_preferred?: string[];
    keywords?: string[];
    seniority?: string;
    location?: string[];
    location_model?: string;
    team?: string;
    project?: string;
    ai_tools?: string[];
    confidence: number;
    search_query?: string;
  };
  /** Structured criteria from full-chat summarizer (SSE). */
  brief?: SearchBriefCriteria | null;
};

export interface AISearchCriteriaBarProps {
  criteria: AISearchCriteriaPayload;
  resultCount: number;
  isSearchRefreshing?: boolean;
}

const Chip: React.FC<{ label: string; className?: string; title?: string }> = ({
  label,
  className = '',
  title,
}) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
    title={title}
  >
    {label}
  </span>
);

export const AISearchCriteriaBar: React.FC<AISearchCriteriaBarProps> = ({
  criteria,
  resultCount,
  isSearchRefreshing = false,
}) => {
  const { extracted, searchQuery, brief } = criteria;

  const skillLabelsLower = new Set(
    [...(extracted?.skills_required || []), ...(extracted?.skills_preferred || [])]
      .map((s) => String(s).trim().toLowerCase())
      .filter(Boolean)
  );
  const keywordChips = (extracted?.keywords || [])
    .filter((k) => k && String(k).trim().length > 0 && !skillLabelsLower.has(String(k).trim().toLowerCase()))
    .filter((k) => !BROADEN_KEYWORD_NOISE.has(String(k).trim().toLowerCase()));

  const accumulatedQuery = (extracted as { search_query?: string })?.search_query?.trim();
  const displaySearchQuery =
    accumulatedQuery && isBroadenOnlySearchQuery(String(searchQuery || '').trim())
      ? accumulatedQuery
      : searchQuery;

  const hasBriefTree = briefHasRenderableTree(brief ?? null);
  const briefSemantic =
    brief?.semantic?.enabled && typeof brief.semantic.query === 'string' && brief.semantic.query.trim().length > 0;

  const hasLegacyFields = !!(
    isUsableExtractedRole(extracted?.role) ||
    (extracted?.skills_required && extracted.skills_required.length > 0) ||
    (extracted?.skills_preferred && extracted.skills_preferred.length > 0) ||
    keywordChips.length > 0 ||
    extracted?.seniority ||
    (extracted?.location && extracted.location.length > 0) ||
    extracted?.location_model ||
    extracted?.team ||
    extracted?.project ||
    (extracted?.ai_tools && extracted.ai_tools.length > 0)
  );

  const conf = extracted?.confidence ?? brief?.confidence ?? 0;

  return (
    <div
      data-testid="ai-search-criteria-bar"
      className={`bg-indigo-900/20 border rounded-xl px-4 py-3 mb-4 transition-[border-color,box-shadow] duration-300 ${
        isSearchRefreshing
          ? 'border-cyan-400/50 shadow-[0_0_20px_-4px_rgba(34,211,238,0.25)]'
          : 'border-indigo-500/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
          <span className="text-base" aria-hidden="true">
            &#10024;
          </span>
          <span className="text-sm font-medium text-indigo-300">AI Search Criteria</span>
          {isSearchRefreshing && (
            <span
              data-testid="ai-search-refreshing-badge"
              className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/15 border border-cyan-400/35 px-2 py-0.5 text-[11px] font-medium text-cyan-200"
              role="status"
              aria-live="polite"
            >
              <svg
                className="h-3 w-3 animate-spin text-cyan-300 shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-90"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Updating matches
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 items-center">
          {hasBriefTree && brief?.tree ? (
            <>
              <BriefTree node={brief.tree} />
              {briefSemantic ? (
                <Chip
                  label={`Semantic: ${String(brief?.semantic?.query).slice(0, 80)}${String(brief?.semantic?.query).length > 80 ? '…' : ''}`}
                  className="bg-cyan-900/40 text-cyan-100 border border-cyan-500/30 max-w-full truncate"
                  title="Semantic retrieval emphasis"
                />
              ) : null}
            </>
          ) : briefSemantic ? (
            <Chip
              label={`Semantic: ${String(brief?.semantic?.query).slice(0, 80)}${String(brief?.semantic?.query).length > 80 ? '…' : ''}`}
              className="bg-cyan-900/40 text-cyan-100 border border-cyan-500/30 max-w-full truncate"
              title="Semantic retrieval emphasis"
            />
          ) : hasLegacyFields ? (
            <>
              {isUsableExtractedRole(extracted.role) && (
                <Chip label={`Role: ${extracted.role}`} className="bg-indigo-600/30 text-indigo-200" />
              )}

              {extracted.seniority && (
                <Chip label={extracted.seniority} className="bg-purple-600/30 text-purple-200" />
              )}

              {extracted.skills_required?.map((skill) => (
                <Chip key={`req-${skill}`} label={skill} className="bg-emerald-600/30 text-emerald-200" />
              ))}

              {extracted.skills_preferred?.map((skill) => (
                <Chip
                  key={`pref-${skill}`}
                  label={skill}
                  className="bg-emerald-600/15 text-emerald-300/70 border border-emerald-500/20"
                />
              ))}

              {keywordChips.map((kw) => (
                <Chip
                  key={`kw-${kw}`}
                  label={kw}
                  className="bg-violet-600/25 text-violet-200 border border-violet-500/25"
                  title="Domain or topic from your search"
                />
              ))}

              {extracted.location?.map((loc) => (
                <Chip key={`loc-${loc}`} label={loc} className="bg-amber-600/30 text-amber-200" />
              ))}

              {extracted.location_model && (
                <Chip
                  label={extracted.location_model}
                  className="bg-amber-600/20 text-amber-300/80 border border-amber-500/20"
                />
              )}

              {extracted.ai_tools?.map((tool) => (
                <Chip key={`ai-${tool}`} label={tool} className="bg-sky-600/30 text-sky-200" />
              ))}

              {extracted.team && (
                <Chip label={`Team: ${extracted.team}`} className="bg-gray-600/30 text-gray-200" />
              )}

              {extracted.project && (
                <Chip label={`Project: ${extracted.project}`} className="bg-gray-600/30 text-gray-200" />
              )}
            </>
          ) : (
            displaySearchQuery && (
              <Chip
                label={displaySearchQuery}
                className="bg-indigo-600/20 text-indigo-200 border border-indigo-500/20"
              />
            )
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {conf > 0 && (
            <span
              data-testid="confidence-badge"
              className={`text-xs px-2 py-0.5 rounded-full ${
                conf >= 70 ? 'bg-green-600/30 text-green-300' : conf >= 40 ? 'bg-yellow-600/30 text-yellow-300' : 'bg-red-600/30 text-red-300'
              }`}
            >
              {conf}% conf
            </span>
          )}

          <span
            data-testid="result-count"
            className={`text-xs px-2 py-0.5 rounded-full ${
              isSearchRefreshing ? 'bg-cyan-900/40 text-cyan-200/90' : 'bg-indigo-600/30 text-indigo-200'
            }`}
            title={isSearchRefreshing ? 'Count updates when the new search finishes' : undefined}
          >
            {isSearchRefreshing ? (
              <>
                <span className="tabular-nums">{resultCount}</span>
                <span className="opacity-80"> · …</span>
              </>
            ) : (
              <>
                {resultCount} {resultCount === 1 ? 'match' : 'matches'}
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
